import { useState, type FormEvent } from 'react'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { toast } from 'sonner'
import { FcGoogle } from 'react-icons/fc'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'

interface RegisterFormProps {
  invitationToken?: string
  invitationEmail?: string
}

const RegisterForm = ({ invitationToken, invitationEmail }: RegisterFormProps = {}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const { checkAuth } = useAuth()
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    email: invitationEmail || '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  })


  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true)

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${invitationToken ? `?invitation_token=${invitationToken}` : ''}`,
        },
      })

      if (error) {
        console.error('Google sign up error:', error)
        toast.error(error.message || 'Failed to sign up with Google')
        setIsGoogleLoading(false)
      }
    } catch (error) {
      console.error('Google sign up failed:', error)
      toast.error('Failed to sign up with Google. Please try again.')
      setIsGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!agreedToTerms) {
      toast.error('Please agree to the privacy policy & terms')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long')
      return
    }

    setIsLoading(true)

    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/complete-registration`,
          data: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            ...(invitationToken ? {
              invitation_token: invitationToken,
            } : {}),
          },
        },
      })

      if (error) {
        toast.error(error.message || 'Failed to register')
        setIsLoading(false)
        return
      }

      if (!data.session) {
        toast.success('Registration successful! Please check your email to confirm your account.')
        navigate('/auth/email-confirmation', { replace: true })
        setIsLoading(false)
        return
      }

      await checkAuth()
    } catch (error) {
      console.error('Registration failed:', error)
      toast.error('Failed to register. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const isFormDisabled = isLoading || isGoogleLoading

  return (
    <div className='space-y-4'>
      {/* Google Sign Up */}
      <Button
        type='button'
        variant='outline'
        className='w-full'
        onClick={handleGoogleSignUp}
        disabled={isFormDisabled}
      >
        <FcGoogle className='size-4' />
        {isGoogleLoading ? 'Redirecting...' : 'Sign up with Google'}
      </Button>

      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <span className='w-full border-t' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-card px-2 text-muted-foreground'>Or continue with</span>
        </div>
      </div>

      <form className='space-y-4' onSubmit={handleSubmit}>
        {/* First Name */}
        <div className='space-y-1'>
          <Label className='leading-5' htmlFor='firstName'>
            First Name*
          </Label>
          <Input
            type='text'
            id='firstName'
            placeholder='Enter your first name'
            value={formData.first_name}
            onChange={handleChange('first_name')}
            required
            disabled={isFormDisabled}
          />
        </div>

        {/* Last Name */}
        <div className='space-y-1'>
          <Label className='leading-5' htmlFor='lastName'>
            Last Name*
          </Label>
          <Input
            type='text'
            id='lastName'
            placeholder='Enter your last name'
            value={formData.last_name}
            onChange={handleChange('last_name')}
            required
            disabled={isFormDisabled}
          />
        </div>

        {/* Email */}
        <div className='space-y-1'>
          <Label className='leading-5' htmlFor='userEmail'>
            Email address*
          </Label>
          <Input
            type='email'
            id='userEmail'
            readOnly={!!invitationToken}
            placeholder='Enter your email address'
            value={formData.email}
            onChange={handleChange('email')}
            required
            disabled={isFormDisabled}
          />
        </div>

        {/* Password */}
        <div className='w-full space-y-1'>
          <Label className='leading-5' htmlFor='password'>
            Password*
          </Label>
          <div className='relative'>
            <Input
              id='password'
              type={isPasswordVisible ? 'text' : 'password'}
              placeholder='••••••••••••••••'
              className='pr-9'
              value={formData.password}
              onChange={handleChange('password')}
              required
              disabled={isFormDisabled}
              minLength={8}
            />
            <Button
              type='button'
              variant='ghost'
              size='icon'
              onClick={() => setIsPasswordVisible(prevState => !prevState)}
              className='text-muted-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 rounded-l-none hover:bg-transparent'
              disabled={isFormDisabled}
            >
              {isPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
              <span className='sr-only'>{isPasswordVisible ? 'Hide password' : 'Show password'}</span>
            </Button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className='w-full space-y-1'>
          <Label className='leading-5' htmlFor='confirmPassword'>
            Confirm Password*
          </Label>
          <div className='relative'>
            <Input
              id='confirmPassword'
              type={isConfirmPasswordVisible ? 'text' : 'password'}
              placeholder='••••••••••••••••'
              className='pr-9'
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              required
              disabled={isFormDisabled}
            />
            <Button
              type='button'
              variant='ghost'
              size='icon'
              onClick={() => setIsConfirmPasswordVisible(prevState => !prevState)}
              className='text-muted-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 rounded-l-none hover:bg-transparent'
              disabled={isFormDisabled}
            >
              {isConfirmPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
              <span className='sr-only'>{isConfirmPasswordVisible ? 'Hide password' : 'Show password'}</span>
            </Button>
          </div>
        </div>

        {/* Privacy policy */}
        <div className='flex items-center gap-3'>
          <Checkbox
            id='terms'
            className='size-6'
            checked={agreedToTerms}
            onCheckedChange={checked => setAgreedToTerms(checked === true)}
            disabled={isFormDisabled}
          />
          <Label htmlFor='terms' className='gap-1'>
            <span className='text-muted-foreground'>I agree to</span>{' '}
            <a href='https://www.fenra.io/privacy-policy' target='_blank' className='hover:underline'>
              Privacy Policy
            </a>
            <span className='text-muted-foreground'> and </span>
            <a href='https://www.fenra.io/terms-of-service' target='_blank' className='hover:underline'>
              Terms of Service
            </a>
          </Label>
        </div>

        <Button className='w-full' type='submit' disabled={isFormDisabled}>
          {isLoading
            ? invitationToken
              ? 'Accepting invitation...'
              : 'Creating account...'
            : invitationToken
              ? 'Accept Invitation & Create Account'
              : 'Sign Up to Fenra'}
        </Button>
      </form>
    </div>
  )
}

export default RegisterForm
